import React from "react";
import { apiRequest } from "../api/requestHelper";
import config from "../../src/config/app.json";
import SectionPage from "./index";
import Modal from "react-modal";
import { useRouter } from "next/router";
import Preview from "../../src/components/Preview";
import useFetchData from "../../src/hooks/useFecthData";
import Head from "next/head";
import styled from "styled-components";
import BreadCrums from "../../src/components/BreadCrums";
import BottomSection from "../../src/containers/BottomSection";
import { createSitemap, getPathName } from "../../src/helpers/utils";

Modal.setAppElement("#__next");

const PreviewPageContainer = styled.div`
  padding: 50px;
  @media (max-width: 425px) {
    padding: 20px;
  }
`;
const BreadCrumbContainer = styled.div`
  padding: 30px 50px 0px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  @media (max-width: 425px) {
    padding: 20px;
  }
`;

const apiUrl = config["APIURIS"][config["APIURIS"]["current"]];
const appUrl = config["TEMPLATEURLS"][config["TEMPLATEURLS"]["current"]];
const appConfigUrl = config["APPURLS"][config["APPURLS"]["current"]];

function TagPage({ data, tag, toShowMessage }) {
  const router = useRouter();
  const { modalData } = useFetchData();

  function getCloseLink() {
    return router.query.continuation
      ? router.query.contextId
        ? `/${router.query.contextId}/${router.query.contextTagId}?continuation=${router.query.continuation}`
        : `/${router.query.id}/${router.query.tagId}?continuation=${router.query.continuation}`
      : router.query.contextId
      ? `/${router.query.contextId}/${router.query.contextTagId}`
      : `/${router.query.id}/${router.query.tagId}`;
  }

  function getPageScripts() {
    const id = router.query.id;
    const tagId = router.query.tagId;
    const name = getPathName(id);
    const tagName = getPathName(tagId);
    return JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        name: "Breadcrumb",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://www.picmaker.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Templates",
            item: "https://www.picmaker.com/templates",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: `${name}`,
            item: `https://www.picmaker.com/templates/${id}`,
          },
          {
            "@type": "ListItem",
            position: 4,
            name: `${tagName}`,
            item: `https://www.picmaker.com/templates/${id}/${tagId}`,
          },
        ],
      },
    ]);
  }

  return router.query.id === "p" && router.query.origin !== "tag-page" ? (
    <>
      <Head>
        <title>{data["design"]["name_en"]}</title>
        <meta property="og:title" content={data["design"]["name_en"]} />
        <meta
          property="og:image"
          content={`${appConfigUrl}landing/og/${data["design"]["thumb"]}`}
        />
        <meta
          property="og:image:url"
          content={`${appConfigUrl}landing/og/${data["design"]["thumb"]}`}
        />
        <meta property="og:image:width" content={"140"} />
        <meta property="og:image:height" content={"140"} />
        <meta property="og:image:type" content={"image/jpeg"} />
        <link rel="canonical" href={`${appUrl}/templates${router.asPath}`} />
        <meta name="description" content={data["meta_description"]} />
        <meta
          property="og:url"
          content={`${appConfigUrl}landing/og/${data["design"]["thumb"]}`}
        />
        <meta property="og:description" content={data["meta_description"]} />
      </Head>
      <>
        <BreadCrumbContainer>
          <BreadCrums
            isMobile={true}
            category={data.category_url}
            templateName={data.design.name_en}
          />
        </BreadCrumbContainer>
        <PreviewPageContainer>
          <Preview data={data} pageStyle="preview-page" />
        </PreviewPageContainer>
        <BottomSection trackId={data["trackid"]} />
      </>
    </>
  ) : (
    <>
      <Head>
        <link rel="canonical" href={`${appUrl}/templates${router.asPath}`} />
        {router.query.id && router.query.tagId && (
          <script type="application/ld+json">{getPageScripts()}</script>
        )}
      </Head>
      <SectionPage data={data} tag={tag} toShowMessage={toShowMessage} />
      <Modal
        isOpen={!!router.query.templateId}
        onRequestClose={() =>
          router.push(getCloseLink(), undefined, { shallow: true })
        }
        className="modal"
        overlayClassName="overlay"
      >
        <Preview data={modalData} allData={data} closeLink={getCloseLink()} />
      </Modal>
    </>
  );
}

export default React.memo(TagPage);

export async function getServerSideProps(context) {
  const { params, query, res } = context;
  const { id, tagId } = params;

  let tag = "";
  if (id === "sitemap") {
    const { data } = await apiRequest(
      "get",
      `${apiUrl}dynamic/sitemap/${tagId.split(".")[0]}`
    );
    if (!data["data"]) {
      return {
        notFound: true,
      };
    }
    const urlList = data["data"]["urls"];
    res.setHeader("Content-Type", "text/xml");
    res.write(createSitemap(urlList));
    res.end();

    return { props: {} };
  }

  const fetchQuery = query.continuation
    ? () => {
        return {
          method: "post",
          url: `${apiUrl}dynamic/templates/`,
          options: {
            category_url: query.contextId ? query.contextId : query.id,
            from: query.continuation || 0,
            size: 30,
            tags: query.contextTagId
              ? [query.contextTagId]
              : query.tagId
              ? [query.tagId]
              : [],
          },
        };
      }
    : query.id === "p" && query.origin !== "tag-page"
    ? () => {
        return {
          method: "get",
          url: `${apiUrl}dynamic/designs/${tagId}`,
        };
      }
    : () => {
        return {
          method: "post",
          url: `${apiUrl}dynamic/templates/`,
          options: {
            category_url: query.contextId ? query.contextId : query.id,
            from: 0,
            size: 30,
            tags: query.contextTagId
              ? [query.contextTagId]
              : query.tagId
              ? [query.tagId]
              : [],
          },
        };
      };

  const { data } = await apiRequest(
    fetchQuery().method,
    fetchQuery().url,
    fetchQuery().options
  );

  if (
    typeof data["data"] === "undefined" &&
    (typeof data["data"]["templates"] === "undefined" ||
      typeof data["data"]["all_templates"] === "undefined")
  ) {
    throw new Error(
      `Fetch Query Url: ${fetchQuery().url} data_category: ${
        fetchQuery().options.category_url
      } data_tag: ${fetchQuery().options.tags}`
    );
  }

  if (query.id === "p" && query.origin !== "tag-page") {
    if (!data["data"]) {
      return {
        notFound: true,
      };
    }

    if (
      !!data["data"] &&
      !!data["data"]["design"] &&
      data["data"]["design"]["template_url"] !== query.tagId
    ) {
      return {
        redirect: {
          destination: `/p/${data["data"]["design"]["template_url"]}`,
        },
      };
    }
  } else {
    if (!data["data"]["all_templates"]) {
      if (
        Object.keys(data["data"]).length <= 0 ||
        data["data"].seo_data.length <= 0 ||
        Object.keys(data["data"].templates).length <= 0
      ) {
        return {
          notFound: true,
        };
      }
    }
  }

  const tempData =
    query.id === "p" && query.origin !== "tag-page"
      ? data["data"]
      : {
          seo_data: data["data"]["seo_data"],
          templates:
            Object.keys(data["data"]["templates"]).length > 0
              ? data["data"]["templates"]
              : data["data"]["all_templates"],
          trackId: data["data"]["trackid"],
          category_details: data["data"]["design_category"],
        };
  const toShowMessage = data["data"]["all_templates"] ? true : false;

  return {
    props: {
      data: tempData,
      tag: tag,
      toShowMessage,
    },
  };
}
